// ============================================================
// Chess Arena — Daily Task Manager (GDD Section 6.5)
// ============================================================

import { GameEvent, ActiveTask, TaskConfig } from '../core/types';
import { playerState } from '../core/playerState';
import eventBus from '../core/eventBus';

// --- Default task pool (used before GameConfig loads DailyTasks.json) ---

const DEFAULT_TASK_POOL: TaskConfig[] = [
  // Easy
  { taskId: 'play_1',    description: 'Play 1 match',              difficulty: 'easy',   trackingEvent: 'match_played',   trackingFilter: null, target: 1,  reward: { coins: 25, xp: 25 }, requiresMinLevel: 1,  requiresMechanic: null, weight: 10 },
  { taskId: 'win_1',     description: 'Win 1 match',               difficulty: 'easy',   trackingEvent: 'match_won',      trackingFilter: null, target: 1,  reward: { coins: 25, xp: 25 }, requiresMinLevel: 1,  requiresMechanic: null, weight: 10 },
  { taskId: 'capture_3', description: 'Capture 3 enemy pieces',    difficulty: 'easy',   trackingEvent: 'piece_captured', trackingFilter: null, target: 3,  reward: { coins: 25, xp: 25 }, requiresMinLevel: 1,  requiresMechanic: null, weight: 10 },
  { taskId: 'spawn_3',   description: 'Spawn 3 reinforcements',    difficulty: 'easy',   trackingEvent: 'piece_spawned',  trackingFilter: null, target: 3,  reward: { coins: 25, xp: 25 }, requiresMinLevel: 1,  requiresMechanic: null, weight: 10 },
  { taskId: 'move_10',   description: 'Move 10 pieces',            difficulty: 'easy',   trackingEvent: 'piece_moved',    trackingFilter: null, target: 10, reward: { coins: 25, xp: 25 }, requiresMinLevel: 1,  requiresMechanic: null, weight: 10 },

  // Medium
  { taskId: 'win_2',              description: 'Win 2 matches',              difficulty: 'medium', trackingEvent: 'match_won',      trackingFilter: null,                      target: 2, reward: { coins: 50, xp: 50 }, requiresMinLevel: 1,  requiresMechanic: null,      weight: 10 },
  { taskId: 'capture_knights_2',  description: 'Capture 2 Knights',          difficulty: 'medium', trackingEvent: 'piece_captured', trackingFilter: { pieceType: 'Knight' },   target: 2, reward: { coins: 50, xp: 50 }, requiresMinLevel: 1,  requiresMechanic: null,      weight: 10 },
  { taskId: 'capture_rooks_1',    description: 'Capture 1 Rook',             difficulty: 'medium', trackingEvent: 'piece_captured', trackingFilter: { pieceType: 'Rook' },     target: 1, reward: { coins: 50, xp: 50 }, requiresMinLevel: 5,  requiresMechanic: null,      weight: 10 },
  { taskId: 'use_premove_3',      description: 'Queue 3 premoves',           difficulty: 'medium', trackingEvent: 'premove_queued', trackingFilter: null,                      target: 3, reward: { coins: 50, xp: 50 }, requiresMinLevel: 2,  requiresMechanic: 'premove', weight: 10 },
  { taskId: 'capture_from_bush_1',description: 'Capture an enemy from a bush', difficulty: 'medium', trackingEvent: 'piece_captured', trackingFilter: { context: 'bush' },     target: 1, reward: { coins: 50, xp: 50 }, requiresMinLevel: 4,  requiresMechanic: 'bush',    weight: 8  },
  { taskId: 'win_streak_2',       description: 'Win 2 matches in a row',     difficulty: 'medium', trackingEvent: 'win_streak',     trackingFilter: null,                      target: 2, reward: { coins: 50, xp: 50 }, requiresMinLevel: 1,  requiresMechanic: null,      weight: 8  },
  { taskId: 'use_reroll_2',       description: 'Use Reroll ability 2 times', difficulty: 'medium', trackingEvent: 'ability_used',   trackingFilter: { abilityId: 'reroll' },   target: 2, reward: { coins: 50, xp: 50 }, requiresMinLevel: 6,  requiresMechanic: 'reroll',  weight: 8  },

  // Hard
  { taskId: 'win_3',              description: 'Win 3 matches',              difficulty: 'hard',   trackingEvent: 'match_won',      trackingFilter: null,                      target: 3,  reward: { coins: 75, xp: 75 }, requiresMinLevel: 1,  requiresMechanic: null,  weight: 10 },
  { taskId: 'capture_queen_1',    description: 'Capture an enemy Queen',     difficulty: 'hard',   trackingEvent: 'piece_captured', trackingFilter: { pieceType: 'Queen' },    target: 1,  reward: { coins: 75, xp: 75 }, requiresMinLevel: 9,  requiresMechanic: null,  weight: 10 },
  { taskId: 'earn_trophies_30',   description: 'Earn 30 trophies',           difficulty: 'hard',   trackingEvent: 'trophies_earned',trackingFilter: null,                      target: 30, reward: { coins: 75, xp: 75 }, requiresMinLevel: 1,  requiresMechanic: null,  weight: 10 },
  { taskId: 'capture_8',          description: 'Capture 8 enemy pieces',     difficulty: 'hard',   trackingEvent: 'piece_captured', trackingFilter: null,                      target: 8,  reward: { coins: 75, xp: 75 }, requiresMinLevel: 1,  requiresMechanic: null,  weight: 10 },
  { taskId: 'win_no_king_damage', description: 'Win without your King being attacked', difficulty: 'hard', trackingEvent: 'match_won', trackingFilter: { kingDamaged: 'false' }, target: 1, reward: { coins: 75, xp: 75 }, requiresMinLevel: 1, requiresMechanic: null, weight: 8 },
];

const DAILY_CHEST_REWARD = { coins: 150, xp: 100 };

class DailyTaskManager {
  private taskPool: TaskConfig[] = DEFAULT_TASK_POOL;
  private _initialized = false;

  /**
   * Initialize the daily task system.
   * Checks if tasks need reset (midnight UTC) and selects new tasks if needed.
   * Optionally accepts a custom task pool (from GameConfig).
   */
  init(customTaskPool?: TaskConfig[]): void {
    if (customTaskPool && customTaskPool.length > 0) {
      this.taskPool = customTaskPool;
    }

    // Check if tasks need reset
    const todayUTC = this.getTodayUTCString();
    if (playerState.dailyTasksLastReset !== todayUTC) {
      this.resetAndSelectTasks(todayUTC);
    }

    // Subscribe to game events for tracking
    this.subscribeToEvents();

    this._initialized = true;
  }

  get isInitialized(): boolean {
    return this._initialized;
  }

  /**
   * Handle a game event — increment progress on matching tasks.
   */
  onGameEvent(event: GameEvent): void {
    const tasks = playerState.dailyTasks;
    let changed = false;

    for (const task of tasks) {
      if (task.claimed) continue;
      if (task.progress >= task.config.target) continue;

      if (task.config.trackingEvent !== event.type) continue;

      // Check filter match
      if (task.config.trackingFilter && event.data) {
        const filterMatch = Object.entries(task.config.trackingFilter).every(
          ([key, value]) => String(event.data![key]) === value
        );
        if (!filterMatch) continue;
      } else if (task.config.trackingFilter && !event.data) {
        continue;
      }

      // For additive events (trophies_earned), add the amount
      if (event.type === 'trophies_earned' && event.data?.amount) {
        task.progress += event.data.amount as number;
      } else {
        task.progress += 1;
      }

      task.progress = Math.min(task.progress, task.config.target);
      changed = true;
    }

    if (changed) {
      playerState.setDailyTasks(tasks);
    }
  }

  /**
   * Claim a completed task's rewards.
   * Returns the reward or null if the task is not claimable.
   */
  claimTask(taskId: string): { coins: number; xp: number } | null {
    const tasks = playerState.dailyTasks;
    const task = tasks.find(t => t.config.taskId === taskId);

    if (!task) return null;
    if (task.claimed) return null;
    if (task.progress < task.config.target) return null;

    task.claimed = true;
    playerState.setDailyTasks(tasks);

    // Award rewards
    playerState.addCoins(task.config.reward.coins);
    playerState.addXP(task.config.reward.xp);

    return { ...task.config.reward };
  }

  /**
   * Claim the daily chest bonus (all 3 tasks completed).
   * Returns the reward or null if not ready.
   */
  claimDailyChest(): { coins: number; xp: number } | null {
    if (!this.isDailyChestReady()) return null;
    if (playerState.dailyChestClaimed) return null;

    playerState.setDailyChestClaimed(true);
    playerState.addCoins(DAILY_CHEST_REWARD.coins);
    playerState.addXP(DAILY_CHEST_REWARD.xp);

    return { ...DAILY_CHEST_REWARD };
  }

  /**
   * Get the current 3 active tasks with progress.
   */
  getActiveTasks(): ActiveTask[] {
    return playerState.dailyTasks;
  }

  /**
   * Are all 3 tasks completed (regardless of claim status)?
   */
  isDailyChestReady(): boolean {
    const tasks = playerState.dailyTasks;
    if (tasks.length < 3) return false;
    return tasks.every(t => t.progress >= t.config.target);
  }

  /**
   * Time in milliseconds until midnight UTC (task reset).
   */
  getResetCountdown(): number {
    const now = new Date();
    const midnight = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + 1,
      0, 0, 0, 0
    ));
    return midnight.getTime() - now.getTime();
  }

  // --- Private ---

  private resetAndSelectTasks(todayUTC: string): void {
    const completedLevel = playerState.highestLevelCompleted;

    // Filter eligible tasks based on player progress
    const eligible = this.taskPool.filter(t => {
      if (t.requiresMinLevel > completedLevel) return false;
      if (t.requiresMechanic && !this.isMechanicUnlocked(t.requiresMechanic, completedLevel)) return false;
      return true;
    });

    const easy = eligible.filter(t => t.difficulty === 'easy');
    const medium = eligible.filter(t => t.difficulty === 'medium');
    const hard = eligible.filter(t => t.difficulty === 'hard');

    const selected: TaskConfig[] = [];
    const usedIds = new Set<string>();

    const pick = (pool: TaskConfig[]): TaskConfig | null => {
      const available = pool.filter(t => !usedIds.has(t.taskId));
      if (available.length === 0) return null;
      return this.weightedRandom(available);
    };

    const easyPick = pick(easy);
    if (easyPick) { selected.push(easyPick); usedIds.add(easyPick.taskId); }

    const mediumPick = pick(medium);
    if (mediumPick) { selected.push(mediumPick); usedIds.add(mediumPick.taskId); }

    const hardPick = pick(hard);
    if (hardPick) { selected.push(hardPick); usedIds.add(hardPick.taskId); }

    const activeTasks: ActiveTask[] = selected.map(config => ({
      config,
      progress: 0,
      claimed: false,
    }));

    playerState.setDailyTasks(activeTasks);
    playerState.setDailyTasksLastReset(todayUTC);
    playerState.setDailyChestClaimed(false);
  }

  private weightedRandom(pool: TaskConfig[]): TaskConfig {
    const totalWeight = pool.reduce((sum, t) => sum + t.weight, 0);
    let roll = Math.random() * totalWeight;
    for (const task of pool) {
      roll -= task.weight;
      if (roll <= 0) return task;
    }
    return pool[pool.length - 1];
  }

  /**
   * Check if a mechanic is unlocked based on campaign level.
   * Maps mechanic names to the level that unlocks them.
   */
  private isMechanicUnlocked(mechanic: string, completedLevel: number): boolean {
    const mechanicLevels: Record<string, number> = {
      premove: 2,
      rock: 2,
      bishop: 3,
      bush: 4,
      rook: 5,
      reroll: 6,
      instant_spawn: 8,
      queen: 9,
    };
    const requiredLevel = mechanicLevels[mechanic];
    if (requiredLevel === undefined) return true; // unknown mechanic, assume unlocked
    return completedLevel >= requiredLevel;
  }

  private getTodayUTCString(): string {
    const now = new Date();
    return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
  }

  private subscribeToEvents(): void {
    const trackableEvents = [
      'match_played', 'match_won', 'match_lost',
      'piece_captured', 'piece_moved', 'piece_spawned',
      'premove_queued', 'ability_used',
      'trophies_earned', 'win_streak',
    ] as const;

    for (const eventType of trackableEvents) {
      eventBus.on(eventType, (data?: Record<string, unknown>) => {
        this.onGameEvent({ type: eventType, data });
      });
    }
  }
}

const dailyTaskManager = new DailyTaskManager();
export default dailyTaskManager;
